import { useCallback, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Outlet, useLocation } from 'react-router-dom';
import { RouteRedirect } from '@/routing/RouteRedirect';
import { useStores } from '@/hooks/useStores';
import { useBootRoutesSettled } from '@/routing/bootRoutes';
import { isDrawerPanelView } from '@/shell/drawerPanel';
import { isShellRuntime } from '@/shell/shellDetection';
import { Waiting } from './Waiting';
import { LaunchSettle } from './LaunchSettle';

import { deploymentNeedsChoosing } from '@/features/onboarding/deploymentChoice';
import { signedOutEntryPath } from '@/features/onboarding/firstScreen';
import {
  deviceGetStartedInProgress,
  GET_STARTED_PATH,
  isAppStartPath,
  settleDeviceGetStarted,
} from '@/features/get-started/deviceGetStartedBoot';
import {
  DeploymentWaitScreen,
  DEPLOYMENT_WAIT_OVERDUE_MS,
  DEPLOYMENT_WAIT_QUIET_MS,
} from '@/features/onboarding/DeploymentWaitScreen';
import { PublicFrame } from '@/components/PublicLayout';
import { PublicWorkAreaGate } from '@/features/public-read/PublicWorkAreaGate';
import { TermsAcceptanceScreen } from '@/features/legal/TermsAcceptanceScreenChunk';
import { useLanguageHold } from '@/i18n/LocaleProvider';

/**
 * Time passing is information, so this route measures it.
 *
 * Every wait here is a wait on a deployment that may not exist. Without a clock the screen has only
 * two states — spinner and content — and an app talking to nothing sits in the first one forever
 * looking like it is nearly done.
 */
function useWaitPhase(waiting: boolean) {
  const [phase, setPhase] = useState<'quiet' | 'waiting' | 'overdue'>('quiet');

  useEffect(() => {
    if (!waiting) {
      setPhase('quiet');
      return;
    }
    setPhase('quiet');
    const toWaiting = setTimeout(() => setPhase('waiting'), DEPLOYMENT_WAIT_QUIET_MS);
    const toOverdue = setTimeout(() => setPhase('overdue'), DEPLOYMENT_WAIT_OVERDUE_MS);
    return () => {
      clearTimeout(toWaiting);
      clearTimeout(toOverdue);
    };
  }, [waiting]);

  return phase;
}

export const ProtectedRoute = observer(function ProtectedRoute() {
  const { authStore } = useStores();
  const location = useLocation();

  /**
   * Read once per render rather than once per module: the connect screen restarts the app when the
   * address changes, but forgetting a deployment and coming back must not need a second restart.
   */
  const needsDeployment = deploymentNeedsChoosing();

  useEffect(() => {
    if (needsDeployment) return;
    if (!authStore.initialized) {
      authStore.initialize();
    }
  }, [authStore, needsDeployment]);

  /*
   * F-0581 OB-12: a person who signed in on this device has started before. The boot's settle saw
   * no session on a first start that went to `/login` past the wizard (a link, OB-13), so the
   * session is recorded here once it exists; without it, signing out of the home opened Get started.
   * Inside the wizard the record is already `started`, and this leaves it so.
   *
   * *Mutation:* drop this effect → presence-cookie.spec's sign-out lands on `/get-started`, not
   * `/login`, and "a sign-in past the wizard records the device as started before" goes red.
   */
  useEffect(() => {
    if (authStore.isAuthenticated) settleDeviceGetStarted({ hasSession: true });
  }, [authStore.isAuthenticated]);

  // T-0559: on the first load, the launch screen's code too, so it draws without a fallback.
  const bootRoutesSettled = useBootRoutesSettled();

  /*
   * F-0578 WAIT-9: a launch that drew its wait settles it over the page once the page is in, rather
   * than cutting to it. A launch that never waited has nothing to settle. Inside the iPhone and iPad
   * app the shell's own launch screen covers the web until `contentSettled`, and the settle is the
   * shell's to draw.
   */
  const launchWaited = useRef(false);
  const [settled, setSettled] = useState(false);
  const endSettle = useCallback(() => setSettled(true), []);

  // I-1971: the person's language is known and its catalogue is on its way: nothing of the app is drawn
  // in another language first.
  const languageHeld = useLanguageHold();

  const waitPhase = useWaitPhase(
    !needsDeployment && (!authStore.initialized || authStore.hasRecoverableSession),
  );

  /**
   * Nowhere to go beats nowhere to look.
   *
   * When there is no deployment this install has ever reached, every other branch below is asking a
   * question of a server that is not there. The screen that lets somebody fix that comes first —
   * before the session check, before the spinner, and on the desktop before the shell that used to
   * be handed out unconditionally when it had a local store to render (ADR-050 retired that store;
   * this is what replaces the assumption it carried).
   */
  if (needsDeployment) {
    const redirect = encodeURIComponent(`${location.pathname}${location.search}`);
    return <RouteRedirect to={`/connect?from=gate&redirect=${redirect}`} />;
  }

  /*
   * There is no desktop branch below this line, and there used to be: the desktop was handed the
   * shell unconditionally, without a session check of any kind. That was defensible while it had a
   * local store to render — nothing to sign into — and ADR-050 retired the store. What the bypass
   * left behind was a route named `ProtectedRoute` that protected one of its two surfaces, and a
   * desktop user who had never signed in getting an application shell that sat on "Loading work
   * areas" forever while every request behind it returned 401. No prompt, no error, no way out.
   *
   * Nothing was exposed by it — the API authenticates every request regardless — but a gate that is
   * a no-op on one surface is not a gate, and the silent frozen shell was the visible half.
   * The desktop now answers the same questions as the browser, in the same order.
   */

  if (!authStore.initialized || (!bootRoutesSettled && authStore.isAuthenticated)) {
    launchWaited.current = true;
    if (waitPhase === 'quiet' || authStore.initialized) {
      return (
        // `data-route-pending`: the app shell waits for this to go before it announces the screen.
        <div
          data-route-pending=""
          // I-1234: the whole viewport's centre, where the iPhone shell drew its loading state at
          // launch. F-0578 WAIT-1: a waiting study, never the FEATit mark.
          className="fixed inset-0 flex items-center justify-center"
        >
          {/*
            F-0578 WAIT-18: inside the app the shell's launch cover is over the web until the first
            screen's content is in, drawing this session's study on the same clock. The web draws none
            of its own under it: a second animation is one more thing that can show at the hand-over.
          */}
          {!isShellRuntime() && <Waiting size="launch" />}
        </div>
      );
    }
    // I-2312: drawn outside both layouts, so its own frame draws the page's one main landmark.
    return (
      <PublicFrame>
        <DeploymentWaitScreen
          overdue={waitPhase === 'overdue'}
          onRetry={() => void authStore.retrySessionVerification()}
        />
      </PublicFrame>
    );
  }

  if (!authStore.isAuthenticated) {
    if (authStore.hasRecoverableSession) {
      return (
        <PublicFrame>
          <DeploymentWaitScreen
            failure={authStore.sessionVerificationError}
            overdue={waitPhase === 'overdue'}
            onRetry={() => void authStore.retrySessionVerification()}
          />
        </PublicFrame>
      );
    }
    // F-0338 AC-PV21: an address that names a Public work area is read without an account; anything
    // else is sent to sign in, as it always was. F-0518, AC-FD1: a desktop window opens on the first
    // screen; a browser tab on the sign-in form.
    return (
      <PublicFrame>
        <PublicWorkAreaGate
          signedIn={false}
          fallback={<RouteRedirect to={signedOutEntryPath(location.pathname)} />}
        />
      </PublicFrame>
    );
  }

  /*
   * I-1971: signed in, and the person's language (carried with the session, `sessionLanguage.ts`) is still
   * loading: the launch's wait, with no words — no caption, no slow line, no label in another language —
   * and busy, for at most `LANGUAGE_HOLD_CAP_MS`. Then the app, in the reader's language.
   *
   * *Mutation:* drop this branch → "signs a German account in on an English browser and never draws
   * English" (e2e) is red, the app drawn in English while the German chunk is held back.
   */
  if (languageHeld) {
    launchWaited.current = true;
    return (
      <div
        data-route-pending=""
        data-language-pending=""
        aria-busy="true"
        className="fixed inset-0 flex items-center justify-center"
      >
        {!isShellRuntime() && <Waiting size="launch" silent />}
      </div>
    );
  }

  /*
   * F-0554, AC-TA5: the terms in force are not the ones this account accepted — a provider sign-up
   * on its first arrival, an account from before the terms, or anyone after they changed. One screen
   * and no page of the app, until accepted or signed out. Only an explicit `true` asks: an answer
   * without the field (an older server) is not stopped.
   *
   * *Mutation:* drop this branch → "draws the terms screen, not the app" goes red.
   */
  if (authStore.user?.termsAcceptanceRequired === true) {
    // I-1406: the More drawer's own nav panel is never where the gate is drawn, and shows nothing of
    // the app while it is up: no work area, no pages, no counts. The shell does not open it either.
    if (isDrawerPanelView()) return null;
    return (
      <PublicFrame>
        <TermsAcceptanceScreen />
      </PublicFrame>
    );
  }

  /*
   * F-0581 OB-7: a person who signed in inside Get started and reloads, or closes and reopens the
   * app, is taken back to it, until it is finished. Only from an app start: a link to a page goes to
   * that page.
   */
  if (isAppStartPath(location.pathname) && deviceGetStartedInProgress()) {
    return <RouteRedirect to={GET_STARTED_PATH} />;
  }

  return (
    <>
      <Outlet />
      {launchWaited.current && !settled && !isShellRuntime() && <LaunchSettle onDone={endSettle} />}
    </>
  );
});
